import { css } from "@emotion/css"

const styles = {
  base: css`
    --btn-padding: var(--spacing-xxs) var(--spacing-m);
    --btn-min-width: 64px;

    --btn-font-weight: bold;
    --btn-font-size: 13px;
    --btn-line-height: 22px;

    --btn-bg: transparent;
    --btn-bg-hover: var(--light-grey-2);
    --btn-border-color-hover: var(--light-grey);
    --btn-bg-active: var(--light-grey);
    --btn-bg-disabled: var(--light-grey-2);

    --btn-color: inherit;
    --btn-color-active: inherit;
    --btn-color-disabled: var(--disabled-grey);
    --btn-color-focus: inherit;

    --btn-border-radius: 4px;
    --btn-border-color: transparent;
    --btn-border-style: solid;
    --btn-border-width: 1px;

    --btn-focus-ring-color: var(--light-grey);

    --btn-text-transform: capitalize;

    position: relative;
    display: inline-block;
    box-sizing: border-box;
    padding: var(--btn-padding, 8px 16px);
    min-width: var(--btn-min-width, 64px);

    font-family: inherit;
    font-weight: var(--btn-font-weight, normal);
    font-size: var(--btn-font-size, 0.875rem);
    line-height: var(--btn-line-height, 1.2rem);

    text-transform: var(--btn-text-transform, capitalize);
    white-space: nowrap;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    appearance: none;
    text-decoration: none;
    text-align: center;

    background-color: var(--btn-bg, transparent);
    color: var(--btn-color, inherit);

    border-radius: var(--btn-border-radius, 4px);
    border-color: var(--btn-border-color, transparent);
    border-style: var(--btn-border-style, solid);
    border-width: var(--btn-border-width, 1px);

    transition:
      background-color,
      border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    &:hover {
      // needed to override link styles
      text-decoration: none;
      background-color: var(--btn-bg-hover, #f5f5f5);
      border-color: var(--btn-border-color-hover, #e7e6e6);
    }

    &:focus,
    :focus-within {
      outline: none;
      box-shadow: 0 0 0 0.2em var(--btn-focus-ring-color, #e7e6e6);
      color: var(--btn-color-focus);
    }

    &.active {
      background-color: var(--btn-bg-active, #e7e6e6);
      border-color: var(--btn-bg-active, #e7e6e6);
      color: var(--btn-color-active);
    }

    &:disabled {
      cursor: default;
      --btn-font-weight: normal;
      background-color: var(--btn-bg-disabled, #f5f5f5);
      color: var(--btn-color-disabled, #c6c5c5);
    }
    &[aria-disabled="true"] {
      cursor: default;
      --btn-font-weight: normal;
      background-color: var(--btn-bg-disabled, #f5f5f5);
      color: var(--btn-color-disabled, #c6c5c5);
    }
  `,
  root: css`
    .label {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .text {
      text-overflow: ellipsis;
      overflow: hidden;
    }

    // Colors
    &[data-color="primary"] {
      --btn-color: var(--primary-main, #00b1a4);
      --btn-color-focus: var(--primary-main, #00b1a4);
      --btn-color-active: var(--primary-main, #00b1a4);
      --btn-bg-active: var(--primary-light, #e0f5f4);
      --btn-focus-ring-color: var(--primary-light, #e0f5f4);
    }

    &[data-color="destructive"] {
      --btn-color: var(--red, #d93845);
      --btn-color-focus: var(--red, #d93845);
      --btn-color-active: var(--red, #d93845);
      --btn-bg-active: var(--lighter-red, #f7d7da);
      --btn-focus-ring-color: var(--lighter-red, #f7d7da);
    }

    // Variants
    &[data-variant="default"][data-color="default"] {
      --btn-border-color: transparent;
      --btn-bg-hover: var(--white, #fff);
      --btn-border-color-hover: transparent;
      --btn-bg-active: transparent;
      --btn-color-active: var(--primary-main, #00b1a4);
      --btn-color-focus: var(--primary-main, #00b1a4);
      --btn-focus-ring-color: none;
      --btn-color-disabled: var(--grey, #a8a8a8);
      --btn-padding: var(--spacing-xxs);
      .icon {
        color: var(--dark-grey, #808080);
      }
    }
    &:hover[data-variant="default"][data-color="default"] {
      .icon {
        color: var(--black, #000);
      }
    }
    &.active[data-variant="default"][data-color="default"] {
      .icon {
        color: var(--primary-main, #00b1a4);
      }
    }
    &:focus[data-variant="default"][data-color="default"] {
      .icon {
        color: var(--primary-main, #00b1a4);
      }
    }
    &:disabled[data-variant="default"][data-color="default"] {
      background-color: transparent;
      .icon {
        color: var(--grey, #a8a8a8);
      }
    }
    &[data-variant="outlined"] {
      --btn-bg: var(--white, #fff);
      --btn-border-color: var(--disabled-grey, #c6c5c5);
    }
    &[data-variant="outlined"][data-color="primary"] {
      --btn-border-color: var(--primary-main, #00b1a4);
      --btn-bg-hover: var(--btn-bg-hover, #f5f5f5);
      --btn-border-color-hover: var(--primary-main, #00b1a4);
    }
    &:disabled[data-variant="outlined"][data-color="primary"] {
      --btn-border-color: var(--disabled-grey, #c6c5c5);
    }

    &[aria-disabled="true"][data-variant="outlined"][data-color="primary"] {
      --btn-border-color: var(--disabled-grey, #c6c5c5);
    }

    &[data-variant="outlined"][data-color="destructive"] {
      --btn-border-color: var(--red, #d93845);
    }

    &[data-variant="contained"][data-color="default"] {
      --btn-bg: var(--white, #fff);
      --btn-color: var(--black, #000);
      --btn-color-active: var(--black, #000);
      --btn-color-focus: var(--black, #000);
    }
    &[data-variant="contained"][data-color="primary"] {
      --btn-color: var(--white, #fff);
      --btn-color-active: var(--white, #fff);
      --btn-color-focus: var(--white, #fff);
      --btn-bg: var(--primary-main, #00b1a4);
      --btn-bg-hover: var(--primary-light-2, #33c1b6);
      --btn-border-color-hover: var(--primary-light-2, #33c1b6);
      --btn-bg-active: var(--primary-dark, #09a59a);
    }
    &:disabled[data-variant="contained"][data-color="primary"] {
      --btn-border-color-hover: var(--btn-bg-disabled, #f5f5f5);
    }
    &[data-variant="contained"][data-color="destructive"] {
      --btn-color: var(--white, #fff);
      --btn-color-active: var(--white, #fff);
      --btn-color-focus: var(--white, #fff);
      --btn-bg: var(--red, #d93845);
      --btn-bg-hover: var(--ligther-red-2, #e96e78);
      --btn-bg-active: var(--dark-red, #ce2731);

      .icon {
        color: currentColor;
      }
    }

    &[data-weight="normal"] {
      --btn-font-weight: normal;
    }

    // Icons
    .icon {
      color: gray;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      width: 18px;
      height: 18px;
      overflow: hidden;
    }

    &:disabled .icon {
      opacity: 0.6;
    }

    &[aria-disabled="true"] .icon {
      opacity: 0.6;
    }

    .icon-start {
      margin-inline-start: -4px;
      margin-inline-end: 4px;
    }

    .icon-end {
      margin-inline-start: 4px;
      margin-inline-end: -4px;
    }
  `,
  fullWidth: css`
    display: block;
    width: 100%;
  `,

  iconButton: css`
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    --btn-min-width: 32px;
    --btn-padding: var(--spacing-xxs, 4px) var(--spacing-xxs, 4px);
    --btn-border-radius: 50%;

    --btn-color: gray;
    --btn-bg-disabled: transparent;

    // Colors
    &[data-color="primary"] {
      --btn-color: var(--primary-main, #00b1a4);
      --btn-bg-hover: var(--primary-light, #e0f5f4);
      --btn-bg-active: var(--primary-light, #e0f5f4);
      --btn-focus-ring-color: var(--primary-light, #e0f5f4);
    }

    &[data-color="destructive"] {
      --btn-color: var(--red, #d93845);
      --btn-bg-hover: var(--lighter-red, #f7d7da);
      --btn-bg-active: var(--lighter-red, #f7d7da);
      --btn-focus-ring-color: var(--lighter-red, #f7d7da);
    }

    /* .icon {
      color: currentColor;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      width: 18px;
      height: 18px;
      overflow: hidden;
    } */

    &:disabled {
      opacity: 0.6;
    }
  `,
}

export default styles
